import Phaser from 'phaser';
import { PassiveTreeData, PassiveNode, PASSIVE_NODE_SIZES, PASSIVE_RACE_COLORS, PASSIVE_NODE_BORDER_COLORS } from '@shared/types/passiveTree';
import { PASSIVE_TREE_DATA } from '@shared/data/passiveTreeData';
import { GAME_FONT_FAMILY } from '@shared/constants';
import { CharacterRaceId } from '@shared/types/character';
import { SkillTreeSyncData } from '@shared/types/messages';

// Tipo extendido para incluir razas adicionales del árbol de pasivas
type ExtendedRaceId = CharacterRaceId | 'gnome';

// Mapa de raza a headId (primera cabeza masculina de cada raza para el árbol)
const RACE_HEAD_IDS: Record<ExtendedRaceId, number> = {
  human: 1,   // head1
  elf: 21,    // head21
  orc: 41,    // head41
  drow: 61,   // head61
  dwarf: 81,  // head81
  gnome: 101, // head101
};

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return {
    fontFamily: GAME_FONT_FAMILY,
    resolution: 2,
    ...style,
  } as T;
}

type PassiveTreePanelCallbacks = {
  onClose: () => void;
  onAllocateNode: (nodeId: string) => void;
  onResetTree: () => void;
  onRequestSync: () => void;
};

export class PassiveTreePanel {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: PassiveTreePanelCallbacks;
  
  private container: Phaser.GameObjects.Container | null = null;
  private treeContainer: Phaser.GameObjects.Container | null = null;
  private isOpen = false;
  
  // Datos del árbol
  private treeData: PassiveTreeData;
  private playerRace: ExtendedRaceId = 'human';
  
  // Estado de los nodos
  private allocatedNodes: Set<string> = new Set();
  private availablePoints: number = 0;
  private spentPoints: number = 0;
  
  // Botón de reset
  private resetButton: Phaser.GameObjects.Text | null = null;
  
  // Gráficos
  private nodeGraphics: Map<string, Phaser.GameObjects.Container> = new Map();
  private connectionGraphics: Phaser.GameObjects.Graphics | null = null;
  private centerLogo: Phaser.GameObjects.Image | null = null;
  
  // Animaciones de glow para nodos seleccionados
  private glowTweens: Map<string, Phaser.Tweens.Tween> = new Map();
  
  // Control de inicialización de cámara
  private hasInitializedCamera: boolean = false;
  
  // Cámara virtual (scroll/zoom del árbol)
  private treeScrollX: number = 0;
  private treeScrollY: number = 0;
  private treeZoom: number = 0.8;
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private dragStartScrollX: number = 0;
  private dragStartScrollY: number = 0;
  
  // Tooltip
  private tooltip: Phaser.GameObjects.Container | null = null;
  private hoveredNodeId: string | null = null;
  
  // UI elements
  private pointsText: Phaser.GameObjects.Text | null = null;
  private closeButton: Phaser.GameObjects.Text | null = null;
  private planningModeCheckbox: Phaser.GameObjects.Container | null = null;
  
  // Modo planificación (debug/visual sin impactar servidor)
  private isPlanningMode: boolean = false;
  private planningAllocatedNodes: Set<string> = new Set();
  private planningAvailablePoints: number = 100;
  
  // Panel dimensions
  private panelWidth: number = 0;
  private panelHeight: number = 0;
  
  constructor(scene: Phaser.Scene, callbacks: PassiveTreePanelCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.treeData = PASSIVE_TREE_DATA;
  }
  
  /**
   * Inicializa el panel con la raza del personaje
   */
  public initialize(playerRace: CharacterRaceId): void {
    this.playerRace = playerRace as ExtendedRaceId;
    this.buildPanel();
  }
  
  /**
   * Construye el panel completo
   */
  private buildPanel(): void {
    const { width, height } = this.scene.scale;
    this.panelWidth = width - 40;
    this.panelHeight = height - 40;
    
    // Contenedor principal
    this.container = this.scene.add.container(width / 2, height / 2);
    this.container.setDepth(2000);
    this.container.setScrollFactor(0);
    
    // Fondo semi-transparente
    const bg = this.scene.add.rectangle(0, 0, width, height, 0x000000, 0.85);
    bg.setInteractive();
    this.container.add(bg);
    
    // Panel principal
    const panelBg = this.scene.add.rectangle(0, 0, this.panelWidth, this.panelHeight, 0x1a1a2e, 1);
    panelBg.setStrokeStyle(2, 0x4a4a6a);
    this.container.add(panelBg);
    
    // Título
    const title = this.scene.add.text(0, -this.panelHeight / 2 + 25, 'Espíritu', withGameFont({
      fontSize: '24px',
      color: '#FFD700',
    }));
    title.setOrigin(0.5);
    this.container.add(title);
    
    // Puntos disponibles
    this.pointsText = this.scene.add.text(-this.panelWidth / 2 + 20, -this.panelHeight / 2 + 25, 
      `Puntos disponibles: ${this.availablePoints}`, withGameFont({
      fontSize: '16px',
      color: '#00FF00',
    }));
    this.pointsText.setOrigin(0, 0.5);
    this.container.add(this.pointsText);
    
    // Botón de cerrar
    this.closeButton = this.scene.add.text(this.panelWidth / 2 - 20, -this.panelHeight / 2 + 25, '✕', withGameFont({
      fontSize: '24px',
      color: '#FF6666',
    }));
    this.closeButton.setOrigin(0.5);
    this.closeButton.setInteractive({ useHandCursor: true });
    this.closeButton.on('pointerover', () => this.closeButton?.setColor('#FF0000'));
    this.closeButton.on('pointerout', () => this.closeButton?.setColor('#FF6666'));
    this.closeButton.on('pointerup', () => this.hide());
    this.container.add(this.closeButton);
    
    // Botón de reset
    this.resetButton = this.scene.add.text(this.panelWidth / 2 - 100, -this.panelHeight / 2 + 25, '⟲ Reset', withGameFont({
      fontSize: '16px',
      color: '#FFAA00',
    }));
    this.resetButton.setOrigin(0.5);
    this.resetButton.setInteractive({ useHandCursor: true });
    this.resetButton.on('pointerover', () => this.resetButton?.setColor('#FFCC00'));
    this.resetButton.on('pointerout', () => this.resetButton?.setColor('#FFAA00'));
    this.resetButton.on('pointerup', () => this.callbacks.onResetTree());
    this.container.add(this.resetButton);
    
    // Checkbox de modo planificación (parte inferior izquierda)
    this.planningModeCheckbox = this.scene.add.container(
      -this.panelWidth / 2 + 20, 
      this.panelHeight / 2 - 25
    );
    
    // Caja del checkbox: arte dorado (swap empty↔selected). Mismo kit que el DOM
    // (ui/form/checkbox_*). Ver docs/UI_SKINNING.md.
    const checkboxImg = this.scene.add.image(0, 0, 'form_checkbox_empty');
    checkboxImg.setOrigin(0, 0.5);
    checkboxImg.setDisplaySize(16, 16);
    checkboxImg.setName('checkbox');
    this.planningModeCheckbox.add(checkboxImg);
    
    // Texto del checkbox
    const checkboxLabel = this.scene.add.text(22, 0, 'Modo Planificación', withGameFont({
      fontSize: '14px',
      color: '#AAAAAA',
    }));
    checkboxLabel.setOrigin(0, 0.5);
    checkboxLabel.setName('label');
    this.planningModeCheckbox.add(checkboxLabel);
    
    // Hacer interactivo - cubrir checkbox y texto
    const hitArea = this.scene.add.rectangle(0, 0, 180, 24, 0x000000, 0);
    hitArea.setOrigin(0, 0.5);
    hitArea.setInteractive({ useHandCursor: true });
    hitArea.on('pointerdown', () => this.togglePlanningMode());
    this.planningModeCheckbox.add(hitArea);
    
    this.container.add(this.planningModeCheckbox);
    
    // Área del árbol (con máscara)
    const treeAreaY = 30;
    const treeAreaHeight = this.panelHeight - 80;
    const treeAreaWidth = this.panelWidth - 20;
    
    // Crear contenedor del árbol
    this.treeContainer = this.scene.add.container(0, treeAreaY);
    this.container.add(this.treeContainer);
    
    // Crear máscara para el área del árbol
    const maskShape = this.scene.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(
      width / 2 - treeAreaWidth / 2,
      height / 2 + treeAreaY - treeAreaHeight / 2,
      treeAreaWidth,
      treeAreaHeight
    );
    const mask = maskShape.createGeometryMask();
    this.treeContainer.setMask(mask);
    
    // Gráficos para las conexiones
    this.connectionGraphics = this.scene.add.graphics();
    this.treeContainer.add(this.connectionGraphics);
    
    // Logo del juego en el centro del árbol (posición 0,0 del mundo del árbol)
    if (this.scene.textures.exists('logo')) {
      this.centerLogo = this.scene.add.image(0, 0, 'logo');
      this.centerLogo.setAlpha(1.0);
      this.treeContainer.add(this.centerLogo);
    }
    
    // Centrar en el punto de inicio de la raza del jugador (primera vez)
    this.centerOnPlayerStart();
    this.hasInitializedCamera = true;
    
    // Renderizar el árbol
    this.renderTree();
    
    // Configurar input
    this.setupInput(panelBg);
    
    // Marcar el nodo de inicio como asignado
    const startNodeId = this.treeData.startingPoints[this.playerRace];
    if (startNodeId) {
      this.allocatedNodes.add(startNodeId);
    }
    
    // Ocultar inicialmente
    this.container.setVisible(false);
  }
  
  /**
   * Centra la vista en el punto de inicio de la raza del jugador
   */
  private centerOnPlayerStart(): void {
    const startNodeId = this.treeData.startingPoints[this.playerRace];
    if (startNodeId) {
      const startNode = this.treeData.nodes.find((n: PassiveNode) => n.id === startNodeId);
      if (startNode) {
        this.treeScrollX = startNode.x;
        this.treeScrollY = startNode.y;
      }
    }
  }
  
  /**
   * Renderiza todos los nodos y conexiones del árbol
   */
  private renderTree(): void {
    if (!this.treeContainer || !this.connectionGraphics) return;
    
    // Limpiar tweens de animación existentes
    this.glowTweens.forEach(tween => tween.destroy());
    this.glowTweens.clear();
    
    // Limpiar nodos existentes
    this.nodeGraphics.forEach(graphic => graphic.destroy());
    this.nodeGraphics.clear();
    
    // Actualizar posición del logo central
    this.updateLogoPosition();
    
    // Renderizar conexiones
    this.renderConnections();
    
    // Renderizar nodos
    for (const node of this.treeData.nodes) {
      this.renderNode(node);
    }
  }
  
  /**
   * Actualiza solo las posiciones de los elementos (sin recrear) - para arrastre fluido
   */
  private updateTreePositions(): void {
    if (!this.treeContainer || !this.connectionGraphics) return;
    
    // Actualizar posición del logo central
    this.updateLogoPosition();
    
    // Actualizar conexiones (Graphics debe redibujarse)
    this.renderConnections();
    
    // Actualizar posiciones de los nodos existentes
    for (const node of this.treeData.nodes) {
      const container = this.nodeGraphics.get(node.id);
      if (container) {
        const x = (node.x - this.treeScrollX) * this.treeZoom;
        const y = (node.y - this.treeScrollY) * this.treeZoom;
        container.setPosition(x, y);
      }
    }
  }
  
  /**
   * Actualiza la posición del logo central
   */
  private updateLogoPosition(): void {
    if (this.centerLogo) {
      // Centro real del hexágono de nodos principales (según passiveTreeData.ts)
      const treeCenterX = this.treeData.center?.x ?? 500;
      const treeCenterY = this.treeData.center?.y ?? 450;
      
      const logoX = (treeCenterX - this.treeScrollX) * this.treeZoom;
      const logoY = (treeCenterY - this.treeScrollY) * this.treeZoom;
      this.centerLogo.setPosition(logoX, logoY);
      // Escalar el logo con el zoom (usar dimensiones originales de la textura)
      const texture = this.centerLogo.texture;
      const frame = texture.get();
      const originalWidth = frame.width;
      const originalHeight = frame.height;
      // Logo más grande: tamaño base de 600px
      const maxLogoSize = 600;
      const baseScale = Math.min(maxLogoSize / originalWidth, maxLogoSize / originalHeight);
      this.centerLogo.setScale(baseScale * this.treeZoom);
    }
  }
  
  /**
   * Renderiza todas las conexiones entre nodos
   */
  private renderConnections(): void {
    if (!this.connectionGraphics) return;
    
    this.connectionGraphics.clear();
    
    const renderedConnections = new Set<string>();
    
    // Usar set correcto según modo
    const allocated = this.isPlanningMode ? this.planningAllocatedNodes : this.allocatedNodes;
    
    for (const node of this.treeData.nodes) {
      for (const connId of node.connections) {
        const connectionKey = [node.id, connId].sort().join('-');
        if (renderedConnections.has(connectionKey)) continue;
        renderedConnections.add(connectionKey);
        
        const targetNode = this.treeData.nodes.find((n: PassiveNode) => n.id === connId);
        if (!targetNode) continue;
        
        // Determinar si la conexión está activa
        const isActive = allocated.has(node.id) && allocated.has(connId);
        const isPartiallyActive = allocated.has(node.id) || allocated.has(connId);
        
        // Color de la línea
        let lineColor = 0x333344;
        let lineAlpha = 0.5;
        
        if (isActive) {
          lineColor = 0xFFD700;
          lineAlpha = 1;
        } else if (isPartiallyActive) {
          lineColor = 0x666688;
          lineAlpha = 0.8;
        }
        
        this.connectionGraphics.lineStyle(2, lineColor, lineAlpha);
        
        const x1 = (node.x - this.treeScrollX) * this.treeZoom;
        const y1 = (node.y - this.treeScrollY) * this.treeZoom;
        const x2 = (targetNode.x - this.treeScrollX) * this.treeZoom;
        const y2 = (targetNode.y - this.treeScrollY) * this.treeZoom;
        
        this.connectionGraphics.beginPath();
        this.connectionGraphics.moveTo(x1, y1);
        this.connectionGraphics.lineTo(x2, y2);
        this.connectionGraphics.strokePath();
      }
    }
  }
  
  /**
   * Crea un efecto de glow orgánico con múltiples capas animadas de forma asíncrona
   */
  private createOrganicGlow(container: Phaser.GameObjects.Container, nodeId: string, size: number, color: number): void {
    // Crear contenedor para las capas de glow (para poder rotarlo suavemente)
    const glowContainer = this.scene.add.container(0, 0);
    container.addAt(glowContainer, 0);
    
    // Configuración de las capas de glow con diferentes tamaños y fases (50% menos opacidad)
    const layerConfigs = [
      { radiusOffset: 12, baseAlpha: 0.075, alphaRange: 0.05 },
      { radiusOffset: 8, baseAlpha: 0.125, alphaRange: 0.075 },
      { radiusOffset: 5, baseAlpha: 0.175, alphaRange: 0.1 },
    ];
    
    // Crear capas y animaciones
    for (let i = 0; i < layerConfigs.length; i++) {
      const config = layerConfigs[i];
      const glow = this.scene.add.arc(0, 0, size + config.radiusOffset, 0, 360, false, color);
      glow.setFillStyle(color, config.baseAlpha);
      glowContainer.add(glow);
      
      const phaseOffset = i * 400; // Desfase entre capas
      
      // Crear tween con parámetros orgánicos - cada capa tiene duraciones diferentes
      const tween = this.scene.tweens.add({
        targets: glow,
        alpha: {
          value: config.baseAlpha + config.alphaRange,
          duration: 2000 + (i * 300),
          ease: 'Sine.easeInOut',
          yoyo: true,
          repeat: -1,
        },
        scaleX: {
          value: 1.08 + (i * 0.02),
          duration: 2500 + (i * 400),
          ease: 'Sine.easeInOut',
          yoyo: true,
          repeat: -1,
        },
        scaleY: {
          value: 1.06 + (i * 0.015),
          duration: 2800 + (i * 350),
          ease: 'Sine.easeInOut',
          yoyo: true,
          repeat: -1,
        },
        delay: phaseOffset,
      });
      
      // Guardar solo el primer tween para limpieza
      if (i === 0) {
        this.glowTweens.set(nodeId, tween);
      }
    }
    
    // Agregar un efecto sutil de rotación del contenedor de glow para más organicidad
    this.scene.tweens.add({
      targets: glowContainer,
      angle: { from: -1.5, to: 1.5 },
      duration: 4000,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
  }
  
  /**
   * Obtiene la key del icono para un nodo (para keystones y notables con icono definido)
   */
  private getNodeIconKey(node: PassiveNode): string | null {
    // Si el nodo tiene un icono definido, usarlo (para keystones y notables)
    if (node.icon) {
      return node.icon;
    }
    
    // Los nodos de raza usan cabezas, no iconos
    return null;
  }
  
  /**
   * Obtiene la key del sprite de cabeza para un nodo de raza
   */
  private getRaceHeadKey(node: PassiveNode): string | null {
    if (node.isStartingPoint && node.raceAffinity) {
      const headId = RACE_HEAD_IDS[node.raceAffinity as ExtendedRaceId];
      if (headId) {
        return `head${headId}`;
      }
    }
    return null;
  }
  
  /**
   * Renderiza un nodo individual
   */
  private renderNode(node: PassiveNode): void {
    if (!this.treeContainer) return;
    
    const x = (node.x - this.treeScrollX) * this.treeZoom;
    const y = (node.y - this.treeScrollY) * this.treeZoom;
    
    // Tamaño del nodo según tipo
    const baseSize = PASSIVE_NODE_SIZES[node.type];
    const size = baseSize * this.treeZoom;
    
    // Color del nodo
    let fillColor = 0x333344;
    if (node.raceAffinity) {
      fillColor = PASSIVE_RACE_COLORS[node.raceAffinity as ExtendedRaceId] || 0x333344;
    }
    
    // Estado del nodo (usar set correcto según modo)
    const allocated = this.isPlanningMode ? this.planningAllocatedNodes : this.allocatedNodes;
    const isAllocated = allocated.has(node.id);
    const isAvailable = this.canAllocateNode(node.id);
    const isStartingPoint = node.isStartingPoint === true;
    
    // Determinar si el nodo debe mostrar un icono (keystones/notables)
    const iconKey = this.getNodeIconKey(node);
    const hasIcon = iconKey !== null && this.scene.textures.exists(iconKey);
    
    // Determinar si el nodo de raza debe mostrar una cabeza
    const headKey = this.getRaceHeadKey(node);
    const hasHead = headKey !== null && this.scene.textures.exists(headKey);
    
    // Contenedor para el nodo (incluye glow + círculo principal)
    const nodeContainer = this.scene.add.container(x, y);
    
    // Determinar opacidad base según estado
    // Los puntos de inicio siempre tienen 100% de opacidad
    let baseAlpha = isStartingPoint ? 1 : 0.55;
    let strokeColor = 0x555566;
    let strokeAlpha = isStartingPoint ? 1 : 0.7;
    let strokeWidth = isStartingPoint ? 2 : 1.5;
    
    if (isAllocated) {
      baseAlpha = 1;
      // Nodos seleccionados: borde dorado brillante
      strokeColor = 0xFFD700;
      strokeAlpha = 1;
      strokeWidth = 3;
    } else if (isAvailable) {
      baseAlpha = isStartingPoint ? 1 : 0.7;
      strokeColor = 0x88FF88;
      strokeAlpha = 0.9;
      strokeWidth = 2;
    }
    
    // Para nodos seleccionados: glow animado orgánico por detrás
    if (isAllocated) {
      this.createOrganicGlow(nodeContainer, node.id, size, 0xFFFFFF);
    }
    
    // Crear efecto de suavizado/antialiasing con capas de glow
    // Capa exterior muy tenue para suavizar bordes
    const glowOuter = this.scene.add.arc(0, 0, size + 3, 0, 360, false, fillColor);
    glowOuter.setFillStyle(fillColor, baseAlpha * 0.15);
    nodeContainer.add(glowOuter);
    
    // Capa media de glow
    const glowMid = this.scene.add.arc(0, 0, size + 1.5, 0, 360, false, fillColor);
    glowMid.setFillStyle(fillColor, baseAlpha * 0.3);
    nodeContainer.add(glowMid);
    
    // Círculo principal del nodo (mantener color de fondo siempre)
    const circle = this.scene.add.arc(0, 0, size, 0, 360, false, fillColor);
    circle.setFillStyle(fillColor, baseAlpha);
    circle.setStrokeStyle(strokeWidth, strokeColor, strokeAlpha);
    nodeContainer.add(circle);
    
    // Agregar cabeza de raza si es un nodo de inicio de raza
    if (hasHead && headKey) {
      // Usar sprite con frame 0 (mirando abajo) de la cabeza
      const headSprite = this.scene.add.sprite(0, 0, headKey, 0);
      // Escalar para que quepa bien en el nodo (x1.5 del tamaño original)
      const headMaxSize = size * 2 * 0.85;
      const headScale = Math.min(headMaxSize / headSprite.width, headMaxSize / headSprite.height) * 1.5;
      headSprite.setScale(headScale);
      headSprite.setAlpha(baseAlpha);
      nodeContainer.add(headSprite);
    }
    
    // Agregar nombre de la raza debajo del nodo (siempre para nodos de inicio)
    if (isStartingPoint) {
      const raceLabel = this.scene.add.text(0, size + 8, node.name, withGameFont({
        fontSize: '11px',
        color: '#FFFFFF',
      }));
      raceLabel.setOrigin(0.5, 0);
      raceLabel.setAlpha(baseAlpha);
      nodeContainer.add(raceLabel);
    }
    // Agregar icono si corresponde (para keystones con icono definido). El icono LLENA
    // el círculo (cover, sin padding: no se ve el color de fondo entre icono y borde);
    // los bordes oscuros del arte se funden con el fondo del árbol. El aro se redibuja
    // encima para que el borde quede crisp.
    else if (hasIcon && iconKey) {
      const icon = this.scene.add.image(0, 0, iconKey);
      const iconTarget = size * 2; // diámetro completo → cubre el círculo
      const iconScale = Math.max(iconTarget / icon.width, iconTarget / icon.height);
      icon.setScale(iconScale);
      icon.setAlpha(baseAlpha);
      nodeContainer.add(icon);
      // Aro por encima del icono SOLO si hay estado (asignado/disponible). Sin estado el
      // icono va sin borde (se quitó el aro amarillo permanente del keystone).
      if (isAllocated || isAvailable) {
        const rim = this.scene.add.arc(0, 0, size, 0, 360, false, 0x000000);
        rim.setFillStyle(0x000000, 0);
        rim.setStrokeStyle(strokeWidth + 0.5, strokeColor, 1);
        nodeContainer.add(rim);
      }
    }
    
    // Zona de interacción invisible (soluciona problemas de hitArea en Arc)
    const hitZone = this.scene.add.zone(0, 0, (size + 5) * 2, (size + 5) * 2);
    hitZone.setInteractive({ useHandCursor: isAvailable });
    nodeContainer.add(hitZone);
    
    // Eventos en la zona de interacción
    hitZone.on('pointerover', () => {
      this.hoveredNodeId = node.id;
      // Usar posición actual del contenedor para el tooltip (se actualiza con zoom/pan)
      this.showTooltip(node, nodeContainer);
      if (!isAllocated) {
        circle.setStrokeStyle(3, isAvailable ? 0x00FF00 : 0xFF6666, 1);
        // Efecto de glow más intenso en hover
        glowOuter.setFillStyle(fillColor, baseAlpha * 0.4);
        glowMid.setFillStyle(fillColor, baseAlpha * 0.6);
      }
    });
    
    hitZone.on('pointerout', () => {
      this.hoveredNodeId = null;
      this.hideTooltip();
      // Restaurar estilo
      circle.setStrokeStyle(strokeWidth, strokeColor, strokeAlpha);
      glowOuter.setFillStyle(fillColor, baseAlpha * 0.15);
      glowMid.setFillStyle(fillColor, baseAlpha * 0.3);
    });
    
    hitZone.on('pointerup', () => {
      if (isAvailable && !isAllocated) {
        this.allocateNode(node.id);
      }
    });
    
    this.treeContainer.add(nodeContainer);
    this.nodeGraphics.set(node.id, nodeContainer);
  }
  
  /**
   * Verifica si un nodo puede ser asignado
   */
  private canAllocateNode(nodeId: string): boolean {
    // Usar el set correcto según el modo
    const allocated = this.isPlanningMode ? this.planningAllocatedNodes : this.allocatedNodes;
    const points = this.isPlanningMode ? this.planningAvailablePoints : this.availablePoints;
    
    // Si ya está asignado, no se puede volver a asignar
    if (allocated.has(nodeId)) return false;
    
    // Si no hay puntos disponibles, no se puede asignar
    if (points <= 0) return false;
    
    const node = this.treeData.nodes.find((n: PassiveNode) => n.id === nodeId);
    if (!node) return false;
    
    // Si es nodo de inicio, verificar que sea de la raza del jugador
    if (node.isStartingPoint) {
      return node.raceAffinity === this.playerRace;
    }
    
    // Verificar si tiene al menos un nodo conectado que esté asignado
    // Verificar en las conexiones del nodo actual
    for (const connId of node.connections) {
      if (allocated.has(connId)) {
        return true;
      }
    }
    
    // También verificar si algún nodo asignado tiene a este nodo en sus conexiones
    // (conexiones bidireccionales - el otro nodo puede conectar hacia este)
    for (const allocatedNodeId of allocated) {
      const allocatedNode = this.treeData.nodes.find((n: PassiveNode) => n.id === allocatedNodeId);
      if (allocatedNode && allocatedNode.connections.includes(nodeId)) {
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * Asigna un nodo al árbol del jugador (envía solicitud al servidor)
   */
  private allocateNode(nodeId: string): void {
    if (!this.canAllocateNode(nodeId)) return;
    
    if (this.isPlanningMode) {
      // En modo planificación: solo actualizar visualmente, no enviar al servidor
      this.planningAllocatedNodes.add(nodeId);
      this.planningAvailablePoints--;
      this.updatePointsDisplay();
      this.renderTree();
    } else {
      // Modo normal: enviar solicitud al servidor
      this.callbacks.onAllocateNode(nodeId);
    }
  }
  
  /**
   * Confirma la asignación de un nodo (llamado desde respuesta del servidor)
   */
  public confirmNodeAllocation(nodeId: string, remainingPoints: number): void {
    this.allocatedNodes.add(nodeId);
    this.availablePoints = remainingPoints;
    this.spentPoints = this.allocatedNodes.size;
    
    // Actualizar UI
    this.updatePointsDisplay();
    
    // Re-renderizar el árbol
    this.renderTree();
  }
  
  /**
   * Sincroniza el estado del skill tree con los datos del servidor
   */
  public syncFromServer(data: SkillTreeSyncData): void {
    this.allocatedNodes.clear();
    for (const nodeId of data.allocatedNodes) {
      this.allocatedNodes.add(nodeId);
    }
    
    // Asegurar que el nodo inicial de la raza esté siempre activo
    const startNodeId = this.treeData.startingPoints[this.playerRace];
    if (startNodeId) {
      this.allocatedNodes.add(startNodeId);
    }
    
    this.availablePoints = data.availablePoints;
    this.spentPoints = data.spentPoints;
    
    // Actualizar UI
    this.updatePointsDisplay();
    
    // Re-renderizar el árbol
    this.renderTree();
  }
  
  /**
   * Maneja el reset del skill tree (llamado desde respuesta del servidor)
   */
  public handleTreeReset(availablePoints: number): void {
    this.allocatedNodes.clear();
    this.availablePoints = availablePoints;
    this.spentPoints = 0;
    
    // Agregar el nodo de inicio de la raza
    const startNodeId = this.treeData.startingPoints[this.playerRace];
    if (startNodeId) {
      this.allocatedNodes.add(startNodeId);
    }
    
    this.centerOnPlayerStart();
    this.updatePointsDisplay();
    this.renderTree();
  }
  
  /**
   * Actualiza el texto de puntos disponibles
   */
  private updatePointsDisplay(): void {
    if (this.pointsText) {
      const points = this.isPlanningMode ? this.planningAvailablePoints : this.availablePoints;
      const prefix = this.isPlanningMode ? '[PLAN] ' : '';
      this.pointsText.setText(`${prefix}Puntos disponibles: ${points}`);
      this.pointsText.setColor(points > 0 ? '#00FF00' : '#FF6666');
    }
  }
  
  /**
   * Muestra el tooltip de un nodo
   */
  private showTooltip(node: PassiveNode, nodeContainer: Phaser.GameObjects.Container): void {
    this.hideTooltip();
    
    if (!this.treeContainer) return;
    
    // Usar posición actual del contenedor del nodo
    const x = nodeContainer.x;
    const y = nodeContainer.y;
    
    const padding = 10;
    const lines: { text: string; type: 'title' | 'description' | 'separator' | 'mod-positive' | 'mod-negative' }[] = [];
    
    // Determinar si el nombre parece ser un modificador (empieza con + o -)
    const nameIsModifier = node.name.startsWith('+') || node.name.startsWith('-');
    
    // Mostrar el nombre como título solo si no parece ser un modificador
    // o si es un nodo notable/keystone (que tienen nombres reales)
    if (!nameIsModifier || node.type === 'notable' || node.type === 'keystone') {
      lines.push({ text: node.name, type: 'title' });
    }
    
    // Mostrar descripción solo si:
    // - Existe y es diferente del nombre
    // - No parece ser una lista de mods (no empieza con + o -)
    // - O si no hay modifiers definidos (la descripción es el contenido principal)
    const descriptionIsModList = node.description && (node.description.startsWith('+') || node.description.startsWith('-'));
    const hasModifiers = node.modifiers && node.modifiers.length > 0;
    
    if (node.description && node.description !== node.name && node.description.trim() !== '') {
      // Si la descripción parece mods pero ya hay modifiers, no mostrarla (evitar duplicados)
      if (!descriptionIsModList || !hasModifiers) {
        lines.push({ text: node.description, type: 'description' });
      }
    }
    
    // Modificadores
    if (hasModifiers) {
      if (lines.length > 0) {
        lines.push({ text: '---', type: 'separator' });
      }
      for (const mod of node.modifiers) {
        const sign = mod.value >= 0 ? '+' : '';
        const suffix = mod.isPercentage ? '%' : '';
        const statName = this.getStatDisplayName(mod.stat);
        const modType = mod.value >= 0 ? 'mod-positive' : 'mod-negative';
        lines.push({ text: `${sign}${mod.value}${suffix} ${statName}`, type: modType });
      }
    }
    
    // Crear contenedor del tooltip con offset desde el nodo
    this.tooltip = this.scene.add.container(x + 30, y);
    
    // Calcular dimensiones
    const maxWidth = 200;
    const lineHeight = 18;
    const tooltipHeight = lines.length * lineHeight + padding * 2;
    
    // Fondo
    const bg = this.scene.add.rectangle(0, 0, maxWidth, tooltipHeight, 0x1a1a2e, 0.95);
    bg.setStrokeStyle(1, 0xFFD700);
    bg.setOrigin(0, 0.5);
    this.tooltip.add(bg);
    
    // Textos
    let yOffset = -tooltipHeight / 2 + padding + lineHeight / 2;
    for (const lineData of lines) {
      if (lineData.type === 'separator') {
        const sep = this.scene.add.rectangle(padding, yOffset, maxWidth - padding * 2, 1, 0x666666);
        sep.setOrigin(0, 0.5);
        this.tooltip.add(sep);
      } else {
        let color = '#CCCCCC';
        let fontSize = '12px';
        
        switch (lineData.type) {
          case 'title':
            color = '#FFFFFF';
            fontSize = '14px';
            break;
          case 'description':
            color = '#AAAAAA';
            break;
          case 'mod-positive':
            color = '#00FF00';
            break;
          case 'mod-negative':
            color = '#FF6666';
            break;
        }
        
        const text = this.scene.add.text(padding, yOffset, lineData.text, withGameFont({
          fontSize: fontSize,
          color: color,
          wordWrap: { width: maxWidth - padding * 2 },
        }));
        text.setOrigin(0, 0.5);
        this.tooltip.add(text);
      }
      yOffset += lineHeight;
    }
    
    this.treeContainer.add(this.tooltip);
  }
  
  /**
   * Oculta el tooltip
   */
  private hideTooltip(): void {
    if (this.tooltip) {
      this.tooltip.destroy();
      this.tooltip = null;
    }
  }
  
  /**
   * Obtiene el nombre para mostrar de un stat
   */
  private getStatDisplayName(stat: string): string {
    const names: Record<string, string> = {
      maxHp: 'HP Máximo',
      maxMana: 'Maná Máximo',
      maxStamina: 'Energía Máx.',
      regenHp: 'Regen HP',
      regenMana: 'Regen Maná',
      regenStamina: 'Regen Energía',
      physDef: 'Def. Física',
      magicDef: 'Def. Mágica',
      minDamage: 'Daño Mínimo',
      maxDamage: 'Daño Máximo',
      attackSpeed: 'Vel. Ataque',
      moveSpeed: 'Vel. Movimiento',
      castSpeed: 'Vel. Casteo',
      critChance: 'Prob. Crítico',
      critMultiplier: 'Mult. Crítico',
      evasion: 'Evasión',
      blockChance: 'Prob. Bloqueo',
      lifeLeech: 'Robo de Vida',
      manaLeech: 'Robo de Maná',
      manaReduction: 'Reducción Maná',
      // Porcentuales de recurso / daño
      hpPercent: 'HP Máximo',
      manaPercent: 'Maná Máximo',
      staminaPercent: 'Energía Máx.',
      damagePercent: 'Daño',
      meleeDamagePercent: 'Daño Cuerpo a cuerpo',
      projectileDamagePercent: 'Daño a Distancia',
      spellDamagePercent: 'Daño de Hechizos',
      // Sustain / robo / reflect
      lifeStealPhysical: 'Robo de Vida (Físico)',
      lifeStealMagic: 'Robo de Vida (Mágico)',
      reflectPhysicalDamage: 'Reflejo Físico',
      reflectMagicDamage: 'Reflejo Mágico',
      healingReceived: 'Curación Recibida',
      healingOutput: 'Curación Realizada',
      magicFind: 'Botín Mágico',
      // Control de masas
      impairImmunity: 'Inmune a CC',
      immobilizeOnHitPhysical: 'Inmovilizar al Golpear',
      immobilizeOnHitSpell: 'Inmovilizar con Hechizo',
      immobilizeDurationReduction: 'Resist. Inmovilizar',
      paralyzeDurationReduction: 'Resist. Paralizar',
      // Invocaciones
      summonHpBonus: 'HP de Invocaciones',
      summonPhysicalDamageBonus: 'Daño Fís. Invocado',
      summonMagicDamageBonus: 'Daño Mág. Invocado',
      summonAttackSpeedBonus: 'Vel. Ataque Invocado',
      summonCritChance: 'Prob. Crítico Invocado',
      summonCritMultiplier: 'Mult. Crítico Invocado',
      summonResurrectionSpeed: 'Vel. Resurrección Invocado',
      // Utilidad
      spellRangeBonus: 'Alcance de Hechizos',
      attackRangeLength: 'Alcance de Ataque',
      // Skilling / extracción
      miningSuccessBonus: 'Prob. de Éxito al Minar',
      // Backstab (solo drow, solo dagas)
      backstabCritDagger: 'Crítico de Espalda (Dagas)',
      critDamageReduction: 'Reducción Daño Crítico',
      alwaysCrit: 'Crítico Garantizado',
      noStaminaCostPhysical: 'Sin Costo de Energía',
      noShield: 'No puede usar escudo',
      noMeditate: 'No puede meditar',
      periodicManaRegenPercent: 'Regen Maná Periódica',
      // Bloqueo omnidireccional (flag)
      blockOmnidirectional: 'Bloqueo Omnidireccional',
      // Invisibilidad / sigilo (flags, rama de sombras drow)
      invisibilityNeverExpires: 'Invisibilidad Permanente',
      silentWhileInvisible: 'Sigilo Absoluto',
      seeInvisible: 'Ver Invisibles',
      // Maestría de arma (+% daño físico con un tipo de arma)
      damageWithSword: 'Daño con Espadas',
      damageWithAxe: 'Daño con Hachas',
      damageWithMace: 'Daño con Mazas',
      damageWithDagger: 'Daño con Dagas',
      damageWithKnuckles: 'Daño con Nudilleras',
      damageWithBow: 'Daño con Arcos',
      damageWithCrossbow: 'Daño con Ballestas',
      damageWithThrowable: 'Daño con Arrojadizas',
    };
    return names[stat] || stat;
  }
  
  /**
   * Configura los eventos de input
   */
  private setupInput(panelBg: Phaser.GameObjects.Rectangle): void {
    // Arrastrar para mover el árbol con click izquierdo en el fondo
    panelBg.setInteractive();
    panelBg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Click izquierdo en el fondo (no sobre un nodo)
      if (pointer.leftButtonDown() && !this.hoveredNodeId) {
        this.isDragging = true;
        this.dragStartX = pointer.x;
        this.dragStartY = pointer.y;
        this.dragStartScrollX = this.treeScrollX;
        this.dragStartScrollY = this.treeScrollY;
      }
    });
    
    // También capturar eventos globales para el arrastre
    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.isOpen) return;
      
      // Verificar si está dentro del panel
      const { width, height } = this.scene.scale;
      const panelLeft = width / 2 - this.panelWidth / 2;
      const panelRight = width / 2 + this.panelWidth / 2;
      const panelTop = height / 2 - this.panelHeight / 2;
      const panelBottom = height / 2 + this.panelHeight / 2;
      
      if (pointer.x >= panelLeft && pointer.x <= panelRight && 
          pointer.y >= panelTop && pointer.y <= panelBottom) {
        // Click izquierdo en el fondo (no sobre un nodo)
        if (pointer.leftButtonDown() && !this.hoveredNodeId) {
          this.isDragging = true;
          this.dragStartX = pointer.x;
          this.dragStartY = pointer.y;
          this.dragStartScrollX = this.treeScrollX;
          this.dragStartScrollY = this.treeScrollY;
        }
      }
    });
    
    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isDragging && this.isOpen) {
        const deltaX = (this.dragStartX - pointer.x) / this.treeZoom;
        const deltaY = (this.dragStartY - pointer.y) / this.treeZoom;
        this.treeScrollX = this.dragStartScrollX + deltaX;
        this.treeScrollY = this.dragStartScrollY + deltaY;
        // Usar actualización rápida de posiciones durante el arrastre
        this.updateTreePositions();
      }
    });
    
    this.scene.input.on('pointerup', () => {
      this.isDragging = false;
    });
    
    // Zoom con la rueda del mouse
    this.scene.input.on('wheel', (pointer: Phaser.Input.Pointer, gameObjects: Phaser.GameObjects.GameObject[], deltaX: number, deltaY: number) => {
      if (!this.isOpen) return;
      
      // Verificar si el mouse está sobre el panel
      const { width, height } = this.scene.scale;
      const panelLeft = width / 2 - this.panelWidth / 2;
      const panelRight = width / 2 + this.panelWidth / 2;
      const panelTop = height / 2 - this.panelHeight / 2;
      const panelBottom = height / 2 + this.panelHeight / 2;
      
      if (pointer.x < panelLeft || pointer.x > panelRight || 
          pointer.y < panelTop || pointer.y > panelBottom) {
        return;
      }
      
      const oldZoom = this.treeZoom;
      const zoomDelta = deltaY > 0 ? -0.1 : 0.1;
      // Zoom: mínimo 50%, máximo 100%
      this.treeZoom = Phaser.Math.Clamp(this.treeZoom + zoomDelta, 0.5, 1.0);
      
      if (this.treeZoom !== oldZoom) {
        // Ocultar tooltip al cambiar zoom para evitar que quede en posición incorrecta
        this.hideTooltip();
        this.hoveredNodeId = null;
        this.renderTree();
      }
    });
  }
  
  /**
   * Alterna el modo planificación
   */
  private togglePlanningMode(): void {
    this.isPlanningMode = !this.isPlanningMode;
    
    // Actualizar visual del checkbox
    if (this.planningModeCheckbox) {
      const checkbox = this.planningModeCheckbox.getByName('checkbox') as Phaser.GameObjects.Image;
      const label = this.planningModeCheckbox.getByName('label') as Phaser.GameObjects.Text;
      if (checkbox) {
        checkbox.setTexture(this.isPlanningMode ? 'form_checkbox_selected' : 'form_checkbox_empty');
        checkbox.setDisplaySize(16, 16);
      }
      if (label) {
        label.setColor(this.isPlanningMode ? '#00FF00' : '#AAAAAA');
      }
    }
    
    if (this.isPlanningMode) {
      // Entrar en modo planificación: copiar nodos actuales y dar 100 puntos
      this.planningAllocatedNodes = new Set(this.allocatedNodes);
      this.planningAvailablePoints = 100;
      
      // Asegurar que el nodo de inicio de la raza esté siempre activo
      const startNodeId = this.treeData.startingPoints[this.playerRace];
      if (startNodeId) {
        this.planningAllocatedNodes.add(startNodeId);
      }
    } else {
      // Salir de modo planificación: limpiar nodos de planificación
      this.planningAllocatedNodes.clear();
    }
    
    this.updatePointsDisplay();
    this.renderTree();
  }
  
  /**
   * Muestra el panel
   */
  public show(): void {
    // Si es la primera vez, buildPanel() centra la cámara
    if (!this.container) {
      this.buildPanel();
    }
    
    // Solicitar sincronización del servidor
    this.callbacks.onRequestSync();
    
    // La posición de cámara se conserva durante la sesión (ya se inicializó en buildPanel)
    
    this.isOpen = true;
    this.container?.setVisible(true);
  }
  
  /**
   * Oculta el panel
   */
  public hide(): void {
    this.isOpen = false;
    this.container?.setVisible(false);
    this.hideTooltip();
    this.callbacks.onClose();
  }
  
  /**
   * Alterna la visibilidad del panel
   */
  public toggle(): void {
    if (this.isOpen) {
      this.hide();
    } else {
      this.show();
    }
  }
  
  /**
   * Reinicia el estado del panel (usado solo para inicialización local)
   */
  private resetState(): void {
    // El estado real viene del servidor via syncFromServer()
    // Esto solo se usa para inicialización del UI
    this.centerOnPlayerStart();
    this.updatePointsDisplay();
    this.renderTree();
  }
  
  /**
   * Verifica si el panel está abierto
   */
  public isOpenPanel(): boolean {
    return this.isOpen;
  }
  
  /**
   * Establece la raza del jugador
   */
  public setPlayerRace(race: CharacterRaceId): void {
    const raceChanged = this.playerRace !== race;
    this.playerRace = race as ExtendedRaceId;
    
    // Centrar la cámara en el nuevo nodo inicial si la raza cambió
    if (raceChanged) {
      this.centerOnPlayerStart();
    }
    
    this.updatePointsDisplay();
    this.renderTree();
  }
  
  /**
   * Destruye el panel
   */
  public destroy(): void {
    this.hideTooltip();
    // Limpiar tweens de animación
    this.glowTweens.forEach(tween => tween.destroy());
    this.glowTweens.clear();
    this.nodeGraphics.forEach(container => container.destroy());
    this.nodeGraphics.clear();
    this.connectionGraphics?.destroy();
    this.centerLogo?.destroy();
    this.container?.destroy();
    this.container = null;
    this.treeContainer = null;
    this.planningModeCheckbox = null;
    this.centerLogo = null;
    this.hasInitializedCamera = false;
    this.isPlanningMode = false;
    this.planningAllocatedNodes.clear();
  }
}
